/**
 * API Documentation Page
 * Displays auto-generated API documentation
 */
import { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { 
  Book, 
  Code, 
  Copy, 
  Search, 
  ChevronDown, 
  ChevronRight,
  FileText,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const methodColors = {
  GET: 'bg-emerald-500/20 text-emerald-600 border-emerald-500/30',
  POST: 'bg-blue-500/20 text-blue-600 border-blue-500/30',
  PUT: 'bg-amber-500/20 text-amber-600 border-amber-500/30',
  PATCH: 'bg-purple-500/20 text-purple-600 border-purple-500/30',
  DELETE: 'bg-red-500/20 text-red-600 border-red-500/30'
};

const APIDocs = () => {
  const [overview, setOverview] = useState(null);
  const [analyticsDoc, setAnalyticsDoc] = useState(null);
  const [partnerDoc, setPartnerDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    fetchDocs();
  }, []);

  const fetchDocs = async () => {
    try {
      const [overviewRes, analyticsRes, partnerRes] = await Promise.all([
        fetch(`${API_URL}/api/docs-generator/api-overview`),
        fetch(`${API_URL}/api/docs-generator/analytics-module`),
        fetch(`${API_URL}/api/docs-generator/partner-module`)
      ]);
      
      setOverview(await overviewRes.json());
      setAnalyticsDoc(await analyticsRes.json());
      setPartnerDoc(await partnerRes.json());
    } catch (error) {
      logger.error('Failed to fetch docs:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const filterEndpoints = (endpoints) => {
    if (!searchQuery) return endpoints;
    const query = searchQuery.toLowerCase();
    return endpoints.filter(e => 
      e.path?.toLowerCase().includes(query) ||
      e.description?.toLowerCase().includes(query) ||
      e.name?.toLowerCase().includes(query)
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div 
        className="space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="api-docs-page"
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
              <Book className="w-8 h-8 text-emerald-600" />
              API Documentation
            </h1>
            <p className="text-muted-foreground">Auto-generated from backend modules</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <a href={`${API_URL}/docs`} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                Swagger UI
              </a>
            </Button>
            <Button variant="outline" asChild>
              <a href={`${API_URL}/redoc`} target="_blank" rel="noopener noreferrer">
                <FileText className="w-4 h-4 mr-2" />
                ReDoc
              </a>
            </Button>
          </div>
        </div>

        {/* Stats */}
        {overview?.statistics && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-emerald-600">{overview.statistics.total_endpoints}</p>
                <p className="text-sm text-muted-foreground">Total Endpoints</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-emerald-600">{overview.statistics.methods.GET}</p>
                <p className="text-sm text-muted-foreground">GET</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-blue-600">{overview.statistics.methods.POST}</p>
                <p className="text-sm text-muted-foreground">POST</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-purple-600">{overview.statistics.methods.PATCH + overview.statistics.methods.PUT}</p>
                <p className="text-sm text-muted-foreground">PUT/PATCH</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-red-600">{overview.statistics.methods.DELETE}</p>
                <p className="text-sm text-muted-foreground">DELETE</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-3 h-12 rounded-xl mb-6">
            <TabsTrigger value="all" className="rounded-lg">
              <Code className="w-4 h-4 mr-2" />
              All Endpoints
            </TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-lg">
              Analytics Module
            </TabsTrigger>
            <TabsTrigger value="partner" className="rounded-lg">
              Partner Module
            </TabsTrigger>
          </TabsList>

          {/* All Endpoints Tab */}
          <TabsContent value="all" className="space-y-6">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search endpoints..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-xl"
              />
            </div>

            {/* Categories */}
            {overview?.categories && Object.entries(overview.categories).map(([category, endpoints]) => {
              const filtered = filterEndpoints(endpoints);
              if (filtered.length === 0) return null;
              
              const isExpanded = expandedCategories[category] ?? true;
              
              return (
                <Card key={category}>
                  <CardHeader 
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => toggleCategory(category)}
                  >
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-heading flex items-center gap-2">
                        {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                        {category}
                      </CardTitle>
                      <Badge variant="outline">{filtered.length} endpoints</Badge>
                    </div>
                  </CardHeader>
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                      >
                        <CardContent className="space-y-3">
                          {filtered.map((endpoint, idx) => (
                            <div 
                              key={`${endpoint.path}-${idx}`}
                              className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                            >
                              <div className="flex gap-1 flex-shrink-0">
                                {endpoint.methods?.map(method => (
                                  <Badge key={method} className={`${methodColors[method]} font-mono text-xs`}>
                                    {method}
                                  </Badge>
                                ))}
                              </div>
                              <div className="flex-1 min-w-0">
                                <code className="text-sm font-mono break-all">{endpoint.path}</code>
                                {endpoint.description && (
                                  <p className="text-sm text-muted-foreground mt-1">{endpoint.description}</p>
                                )}
                              </div>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="flex-shrink-0"
                                onClick={(e) => { e.stopPropagation(); copyToClipboard(endpoint.path); }}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </CardContent>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              );
            })}
          </TabsContent>

          {/* Analytics Module Tab */}
          <TabsContent value="analytics" className="space-y-6">
            {analyticsDoc && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading">Analytics Module</CardTitle>
                    <CardDescription>{analyticsDoc.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-sm"><strong>Main Router:</strong> <code className="bg-muted px-2 py-1 rounded">{analyticsDoc.files?.main_router}</code></p>
                    <p className="text-sm"><strong>Helpers:</strong> <code className="bg-muted px-2 py-1 rounded">{analyticsDoc.files?.helpers}</code></p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">API Endpoints</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {analyticsDoc.endpoints?.map((ep, idx) => (
                      <div key={idx} className="p-4 rounded-lg border bg-card">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={methodColors[ep.method]}>{ep.method}</Badge>
                          <code className="font-mono text-sm">{ep.path}</code>
                        </div>
                        <p className="text-sm text-muted-foreground">{ep.description}</p>
                        {ep.params && (
                          <div className="mt-2 text-xs">
                            <strong>Params:</strong> {JSON.stringify(ep.params)}
                          </div>
                        )}
                        {ep.returns && (
                          <div className="mt-2 text-xs">
                            <strong>Returns:</strong> {ep.returns.join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Helper Functions</CardTitle>
                    <CardDescription>Extracted to analytics_helpers.py for maintainability</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {analyticsDoc.helper_functions?.map((fn, idx) => (
                      <div key={idx} className="p-4 rounded-lg border bg-muted/30">
                        <code className="font-mono text-sm font-semibold text-emerald-600">{fn.name}()</code>
                        <p className="text-sm text-muted-foreground mt-1">{fn.description}</p>
                        <div className="mt-2 text-xs space-y-1">
                          <p><strong>Params:</strong> {fn.params.join(', ')}</p>
                          <p><strong>Returns:</strong> {fn.returns}</p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Partner Module Tab */}
          <TabsContent value="partner" className="space-y-6">
            {partnerDoc && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading">Partner Dashboard Module</CardTitle>
                    <CardDescription>{partnerDoc.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-sm"><strong>Main Page:</strong> <code className="bg-muted px-2 py-1 rounded">{partnerDoc.files?.main_page}</code></p>
                    <p className="text-sm"><strong>Components:</strong> <code className="bg-muted px-2 py-1 rounded">{partnerDoc.files?.components_dir}</code></p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">React Components</CardTitle>
                    <CardDescription>Extracted for single-responsibility principle</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {partnerDoc.components?.map((comp, idx) => (
                      <div key={idx} className="p-4 rounded-lg border bg-card">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="bg-purple-500/10 text-purple-600">Component</Badge>
                          <code className="font-mono text-sm font-semibold">{comp.name}</code>
                        </div>
                        <p className="text-sm text-muted-foreground">{comp.description}</p>
                        <p className="text-xs mt-2"><strong>File:</strong> {comp.file}</p>
                        <p className="text-xs"><strong>Props:</strong> {comp.props.join(', ')}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">API Endpoints</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {partnerDoc.api_endpoints?.map((ep, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-muted/50 flex items-center gap-3">
                        <Badge className={methodColors[ep.method?.split('/')[0] || 'GET']}>{ep.method}</Badge>
                        <code className="font-mono text-sm flex-1">{ep.path}</code>
                        <span className="text-sm text-muted-foreground">{ep.description}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </motion.div>
    </Layout>
  );
};

export default APIDocs;
